import { Badge, Center, SegmentedControl } from "@mantine/core";
import type { MantineColor, MantineSize } from "@mantine/core";
import React from "react";

export type SegmentedToggleOption<T extends string = string> = {
  value: T;
  label: React.ReactNode;
  count?: number;
  disabled?: boolean;
};

type SegmentedToggleProps<T extends string = string> = {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedToggleOption<T>[];
  color?: MantineColor;
  size?: MantineSize;
  fullWidth?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
};

const SegmentedToggle = <T extends string = string,>({
  value,
  onChange,
  options,
  color = "mc-primary",
  size,
  fullWidth = false,
  disabled = false,
  ariaLabel,
  className,
  style,
}: SegmentedToggleProps<T>) => {
  const hasCountBadges = options.some((option) => option.count != null);

  const data = options.map((option) => {
    const isActive = value === option.value;
    const labelWithCount =
      option.count != null ? (
        <Center inline style={{ gap: 10, whiteSpace: "nowrap" }} px="sm">
          {typeof option.label === "string" ? <span>{option.label}</span> : option.label}
          <Badge
            h="20px"
            miw={28}
            variant={isActive ? "filled" : "outline"}
            color={isActive ? "blue" : "gray"}
            radius="sm"
            fz={11}
          >
            <span>{option.count}</span>
          </Badge>
        </Center>
      ) : (
        option.label
      );

    return {
      value: option.value,
      disabled: option.disabled,
      label: labelWithCount,
    };
  });

  return (
    <SegmentedControl
      aria-label={ariaLabel}
      value={value}
      color={color}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      className={className}
      style={style}
      styles={hasCountBadges ? { label: { overflow: "visible" } } : undefined}
      onChange={(nextValue) => onChange(nextValue as T)}
      data={data}
    />
  );
};

export default SegmentedToggle;